// apps/web/src/lib/navigation-config.ts

/**
 * Default back paths for each route pattern.
 * Keys are route patterns with :param placeholders.
 * Values are the default back destinations.
 * All paths are WITHOUT locale prefix.
 */
export const DEFAULT_BACK_PATHS: Record<string, string> = {
  // Events
  '/dashboard/events/:eventId': '/dashboard/events',
  '/dashboard/events/:eventId/edit': '/dashboard/events/:eventId',
  '/dashboard/events/:eventId/judges': '/dashboard/events/:eventId',

  // Competition routes (nested under events)
  '/dashboard/events/:eventId/competitions/create': '/dashboard/events/:eventId',
  '/dashboard/events/:eventId/competitions/:competitionId': '/dashboard/events/:eventId',
  '/dashboard/events/:eventId/competitions/:competitionId/settings': '/dashboard/events/:eventId/competitions/:competitionId',
  '/dashboard/events/:eventId/competitions/:competitionId/stages/create': '/dashboard/events/:eventId/competitions/:competitionId',
  '/dashboard/events/:eventId/competitions/:competitionId/stages/:stageId': '/dashboard/events/:eventId/competitions/:competitionId',
  '/dashboard/events/:eventId/competitions/:competitionId/stages/:stageId/edit': '/dashboard/events/:eventId/competitions/:competitionId/stages/:stageId',
  '/dashboard/events/:eventId/competitions/:competitionId/teams/create': '/dashboard/events/:eventId/competitions/:competitionId',
  '/dashboard/events/:eventId/competitions/:competitionId/teams/:teamId': '/dashboard/events/:eventId/competitions/:competitionId',
  '/dashboard/events/:eventId/competitions/:competitionId/teams/:teamId/edit': '/dashboard/events/:eventId/competitions/:competitionId/teams/:teamId',

  // Score Forms (nested under competitions)
  '/dashboard/events/:eventId/competitions/:competitionId/score-forms/create': '/dashboard/events/:eventId/competitions/:competitionId',
  '/dashboard/events/:eventId/competitions/:competitionId/score-forms/:scoreFormId': '/dashboard/events/:eventId/competitions/:competitionId',
  '/dashboard/events/:eventId/competitions/:competitionId/score-forms/:scoreFormId/edit': '/dashboard/events/:eventId/competitions/:competitionId/score-forms/:scoreFormId',

  // Create pages (static routes)
  '/dashboard/events/create': '/dashboard/events',

  // Judge
  '/dashboard/judge/score': '/dashboard/judge',

  // Judge Certification
  '/dashboard/admin/judge-cert/questions': '/dashboard',
  '/dashboard/admin/judge-cert/configs': '/dashboard',

  // Judge Certificates (QR verification)
  '/dashboard/admin/judge-certificates': '/dashboard',

  // Duplicates
  '/dashboard/admin/duplicates': '/dashboard',

  // Profile Issues
  '/dashboard/admin/profile-issues': '/dashboard',

  // Badge Templates
  '/dashboard/admin/badge-templates': '/dashboard',

  '/dashboard/judge-certification': '/dashboard',
  '/dashboard/judge-certification/exam/:attemptId': '/dashboard/judge-certification',
  '/dashboard/judge/certificates': '/dashboard',

  // Preparation
  '/dashboard/preparation/:eventId': '/dashboard/preparation',

  // My Competitions
  '/dashboard/my-competitions/:teamId': '/dashboard/my-competitions',
};

/**
 * Breadcrumb configuration for dashboard routes.
 * Each entry defines a breadcrumb item with its path, label key, and optional parent.
 * Dynamic routes (with :params) are supported.
 */
export interface BreadcrumbConfig {
  path: string;
  labelKey: string;
  parent?: string;
}

export const BREADCRUMB_CONFIG: BreadcrumbConfig[] = [
  // Dashboard root
  { path: '/dashboard', labelKey: 'nav.dashboard' },

  // Users
  { path: '/dashboard/users', labelKey: 'nav.users', parent: '/dashboard' },

  // Admin Profiles
  { path: '/dashboard/admin/profiles', labelKey: 'nav.adminProfiles', parent: '/dashboard' },

  // Admin Profile Issues
  { path: '/dashboard/admin/profile-issues', labelKey: 'nav.profileIssues', parent: '/dashboard' },

  // Profiles
  { path: '/dashboard/profiles', labelKey: 'nav.profiles', parent: '/dashboard' },

  // Competitions (standalone list)
  { path: '/dashboard/competitions', labelKey: 'nav.competitions', parent: '/dashboard' },

  // Schools
  { path: '/dashboard/schools', labelKey: 'nav.schools', parent: '/dashboard' },

  // Events
  { path: '/dashboard/events', labelKey: 'nav.events', parent: '/dashboard' },
  { path: '/dashboard/events/:eventId', labelKey: 'common.viewDetails', parent: '/dashboard/events' },
  { path: '/dashboard/events/:eventId/edit', labelKey: 'common.edit', parent: '/dashboard/events/:eventId' },
  { path: '/dashboard/events/:eventId/judges', labelKey: 'nav.judges', parent: '/dashboard/events/:eventId' },

  // Competitions (nested under events)
  { path: '/dashboard/events/:eventId/competitions/create', labelKey: 'common.create', parent: '/dashboard/events/:eventId' },
  { path: '/dashboard/events/:eventId/competitions/:competitionId', labelKey: 'common.viewDetails', parent: '/dashboard/events/:eventId' },
  { path: '/dashboard/events/:eventId/competitions/:competitionId/settings', labelKey: 'nav.settings', parent: '/dashboard/events/:eventId/competitions/:competitionId' },
  { path: '/dashboard/events/:eventId/competitions/:competitionId/stages/create', labelKey: 'common.create', parent: '/dashboard/events/:eventId/competitions/:competitionId' },
  { path: '/dashboard/events/:eventId/competitions/:competitionId/stages/:stageId', labelKey: 'common.viewDetails', parent: '/dashboard/events/:eventId/competitions/:competitionId' },
  { path: '/dashboard/events/:eventId/competitions/:competitionId/stages/:stageId/edit', labelKey: 'common.edit', parent: '/dashboard/events/:eventId/competitions/:competitionId/stages/:stageId' },
  { path: '/dashboard/events/:eventId/competitions/:competitionId/teams/create', labelKey: 'common.create', parent: '/dashboard/events/:eventId/competitions/:competitionId' },
  { path: '/dashboard/events/:eventId/competitions/:competitionId/teams/:teamId', labelKey: 'common.viewDetails', parent: '/dashboard/events/:eventId/competitions/:competitionId' },
  { path: '/dashboard/events/:eventId/competitions/:competitionId/teams/:teamId/edit', labelKey: 'common.edit', parent: '/dashboard/events/:eventId/competitions/:competitionId/teams/:teamId' },
  { path: '/dashboard/events/:eventId/competitions/:competitionId/score-forms/create', labelKey: 'common.create', parent: '/dashboard/events/:eventId/competitions/:competitionId' },
  { path: '/dashboard/events/:eventId/competitions/:competitionId/score-forms/:scoreFormId', labelKey: 'common.viewDetails', parent: '/dashboard/events/:eventId/competitions/:competitionId' },
  { path: '/dashboard/events/:eventId/competitions/:competitionId/score-forms/:scoreFormId/edit', labelKey: 'common.edit', parent: '/dashboard/events/:eventId/competitions/:competitionId/score-forms/:scoreFormId' },

  // Judge
  { path: '/dashboard/judge', labelKey: 'nav.judge', parent: '/dashboard' },
  { path: '/dashboard/judge/score', labelKey: 'nav.judge', parent: '/dashboard/judge' },

  // Admin Judges
  { path: '/dashboard/admin/judges', labelKey: 'nav.judges', parent: '/dashboard' },

  // Admin Invite Codes
  { path: '/dashboard/admin/invite-codes', labelKey: 'nav.inviteCodes', parent: '/dashboard' },

  // Admin Schools
  { path: '/dashboard/admin/schools', labelKey: 'nav.schools', parent: '/dashboard' },
  { path: '/dashboard/admin/schools/:id', labelKey: 'common.viewDetails', parent: '/dashboard/admin/schools' },

  // Admin Institutions
  { path: '/dashboard/admin/institutions', labelKey: 'nav.institutions', parent: '/dashboard' },
  { path: '/dashboard/admin/institutions/:id', labelKey: 'common.viewDetails', parent: '/dashboard/admin/institutions' },

  // Admin Messages
  { path: '/dashboard/admin/messages', labelKey: 'nav.adminMessages', parent: '/dashboard' },

  // Admin Duplicates
  { path: '/dashboard/admin/duplicates', labelKey: 'nav.duplicates', parent: '/dashboard' },

  // Admin Insurances
  { path: '/dashboard/admin/insurances', labelKey: 'nav.insurances', parent: '/dashboard' },

  // Judge Registration
  { path: '/dashboard/judge-registration', labelKey: 'nav.judgeRegistration', parent: '/dashboard' },

  // Judge Certification
  { path: '/dashboard/admin/judge-cert/questions', labelKey: 'nav.judgeCertQuestions', parent: '/dashboard' },
  { path: '/dashboard/admin/judge-cert/configs', labelKey: 'nav.judgeCertConfigs', parent: '/dashboard' },

  // Judge Certificates (QR verification)
  { path: '/dashboard/admin/judge-certificates', labelKey: 'nav.judgeCertificates', parent: '/dashboard' },

  // Badge Templates
  { path: '/dashboard/admin/badge-templates', labelKey: 'badges.template.title', parent: '/dashboard' },
  { path: '/dashboard/judge-certification', labelKey: 'nav.judgeCertification', parent: '/dashboard' },
  { path: '/dashboard/judge-certification/exam/:attemptId', labelKey: 'judge-certification.exam.title', parent: '/dashboard/judge-certification' },
  { path: '/dashboard/judge/certificates', labelKey: 'nav.judgeMyCertificates', parent: '/dashboard' },

  // Preparation
  { path: '/dashboard/preparation', labelKey: 'nav.preparation', parent: '/dashboard' },
  { path: '/dashboard/preparation/:eventId', labelKey: 'common.viewDetails', parent: '/dashboard/preparation' },

  // My Competitions
  { path: '/dashboard/my-competitions', labelKey: 'nav.myCompetitions', parent: '/dashboard' },
  { path: '/dashboard/my-competitions/:teamId', labelKey: 'common.viewDetails', parent: '/dashboard/my-competitions' },

  // Messages
  { path: '/dashboard/messages', labelKey: 'nav.messages', parent: '/dashboard' },

  // School dashboard
  { path: '/dashboard/school', labelKey: 'nav.mySchool', parent: '/dashboard' },
  { path: '/dashboard/school/profiles', labelKey: 'nav.mySchool.profiles', parent: '/dashboard/school' },
  { path: '/dashboard/school/users', labelKey: 'nav.mySchool.users', parent: '/dashboard/school' },
  { path: '/dashboard/school/settings', labelKey: 'nav.mySchool.settings', parent: '/dashboard/school' },
  { path: '/dashboard/school/awards', labelKey: 'nav.mySchool.awards', parent: '/dashboard/school' },
  { path: '/dashboard/school/enrollments', labelKey: 'nav.mySchool.enrollments', parent: '/dashboard/school' },
  { path: '/dashboard/school/awards/:teamId', labelKey: 'common.viewDetails', parent: '/dashboard/school/awards' },

  // Institution dashboard
  { path: '/dashboard/institution', labelKey: 'nav.myInstitution', parent: '/dashboard' },
  { path: '/dashboard/institution/profiles', labelKey: 'nav.myInstitution.profiles', parent: '/dashboard/institution' },
  { path: '/dashboard/institution/users', labelKey: 'nav.myInstitution.users', parent: '/dashboard/institution' },
  { path: '/dashboard/institution/settings', labelKey: 'nav.myInstitution.settings', parent: '/dashboard/institution' },
  { path: '/dashboard/institution/enrollments', labelKey: 'nav.myInstitution.enrollments', parent: '/dashboard/institution' },
  { path: '/dashboard/institution/awards', labelKey: 'nav.myInstitution.awards', parent: '/dashboard/institution' },
  { path: '/dashboard/institution/awards/:teamId', labelKey: 'common.viewDetails', parent: '/dashboard/institution/awards' },

  // Settings
  { path: '/dashboard/settings', labelKey: 'nav.settings', parent: '/dashboard' },

  // Audit Center (admin only)
  { path: '/dashboard/admin/audit-center', labelKey: 'nav.auditCenter', parent: '/dashboard' },
];

// Pre-compiled regex cache for pattern matching
const PATTERN_REGEX_MAP: Map<string, RegExp> = new Map(
  Object.keys(DEFAULT_BACK_PATHS).map(pattern => {
    const escapedPattern = pattern.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regexPattern = '^' + escapedPattern.replace(/:[^/]+/g, '([^/]+)') + '$';
    return [pattern, new RegExp(regexPattern)];
  })
);

/**
 * Remove locale prefix from a path.
 * Example: '/en-US/dashboard/events' -> '/dashboard/events'
 */
export function stripLocale(path: string): string {
  return path.replace(/^\/[a-z]{2}-[A-Z]{2}/, '') || '/';
}

/**
 * Add locale prefix to a path.
 * Note: Since locale routing was removed from the URL (routes no longer use [locale]),
 * this function now returns the path unchanged. Locale is handled via context.
 * Kept for backwards compatibility in case locale routing is re-enabled.
 */
export function addLocale(path: string, locale: string): string {
  return path;
}

/**
 * Match a path against route patterns and extract parameters.
 * Returns null if no pattern matches.
 */
export function matchPathPattern(
  pathWithoutLocale: string
): { pattern: string; params: Record<string, string> } | null {
  for (const [pattern, regex] of PATTERN_REGEX_MAP) {
    const match = pathWithoutLocale.match(regex);

    if (match) {
      // Extract parameter names from pattern
      const paramNames = pattern.match(/:[^/]+/g)?.map(p => p.slice(1)) || [];
      // Build params object
      const params = Object.fromEntries(
        paramNames.map((name, i) => [name, match[i + 1]])
      );
      return { pattern, params };
    }
  }
  return null;
}

/**
 * Resolve the default back path for a given path.
 * Returns null if no matching pattern is found.
 */
export function resolveDefaultPath(
  pathWithoutLocale: string,
  extraParams?: Record<string, string>
): string | null {
  const match = matchPathPattern(pathWithoutLocale);
  if (!match) return null;

  let resolved = DEFAULT_BACK_PATHS[match.pattern];
  if (!resolved) return null;

  // Replace :param with actual values
  const allParams = { ...match.params, ...extraParams };
  Object.entries(allParams).forEach(([key, value]) => {
    resolved = resolved.replace(`:${key}`, value);
  });

  return resolved;
}

// Pre-compiled regex cache for breadcrumb pattern matching
const BREADCRUMB_PATTERN_REGEX_MAP: Map<string, RegExp> = new Map(
  BREADCRUMB_CONFIG.map(item => {
    const escapedPattern = item.path.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regexPattern = '^' + escapedPattern.replace(/:[^/]+/g, '([^/]+)') + '$';
    return [item.path, new RegExp(regexPattern)];
  })
);

/**
 * Build a lookup map from path pattern -> BreadcrumbConfig
 */
const BREADCRUMB_BY_PATTERN: Map<string, BreadcrumbConfig> = new Map(
  BREADCRUMB_CONFIG.map(item => [item.path, item])
);

/**
 * Match a path against breadcrumb config patterns.
 * Returns the matched BreadcrumbConfig and extracted params.
 */
function matchBreadcrumbPattern(
  pathWithoutLocale: string
): { config: BreadcrumbConfig; params: Record<string, string> } | null {
  for (const [pattern, regex] of BREADCRUMB_PATTERN_REGEX_MAP) {
    const match = pathWithoutLocale.match(regex);

    if (match) {
      const config = BREADCRUMB_BY_PATTERN.get(pattern);
      if (!config) return null;

      // Extract parameter names from pattern
      const paramNames = pattern.match(/:[^/]+/g)?.map(p => p.slice(1)) || [];
      // Build params object
      const params = Object.fromEntries(
        paramNames.map((name, i) => [name, match[i + 1]])
      );
      return { config, params };
    }
  }
  return null;
}

export interface BreadcrumbItem {
  path: string;
  labelKey: string;
  params?: Record<string, string>;
}

/**
 * Resolve a path template with actual parameter values.
 * E.g., '/dashboard/events/:eventId/competitions/:competitionId' with { eventId: '1', competitionId: '123' } becomes '/dashboard/events/1/competitions/123'
 */
function resolvePath(path: string, params: Record<string, string>): string {
  let resolved = path;
  Object.entries(params).forEach(([key, value]) => {
    resolved = resolved.replace(`:${key}`, value);
  });
  return resolved;
}

/**
 * Build the breadcrumb trail for a given path.
 * Returns array of breadcrumb items from root to current page.
 */
export function buildBreadcrumbs(pathWithoutLocale: string): BreadcrumbItem[] {
  const trail: BreadcrumbItem[] = [];

  // First, try exact match for static routes
  const exactConfig = BREADCRUMB_CONFIG.find(item => item.path === pathWithoutLocale);
  if (exactConfig) {
    trail.push({ path: exactConfig.path, labelKey: exactConfig.labelKey });
    // Walk up the parent chain
    let current = exactConfig;
    while (current.parent) {
      const parentConfig = BREADCRUMB_CONFIG.find(item => item.path === current.parent);
      if (parentConfig) {
        trail.unshift({ path: parentConfig.path, labelKey: parentConfig.labelKey });
        current = parentConfig;
      } else {
        break;
      }
    }
    return trail;
  }

  // Try pattern match for dynamic routes
  const match = matchBreadcrumbPattern(pathWithoutLocale);
  if (match) {
    const { config, params } = match;
    // Use resolved path (actual URL with real IDs) instead of template
    const resolvedPath = resolvePath(config.path, params);
    trail.push({ path: resolvedPath, labelKey: config.labelKey, params });

    // Walk up the parent chain, resolving params along the way
    let current = config;
    let currentParams = { ...params };
    while (current.parent) {
      // Resolve parent path with current params
      let parentPath = current.parent;
      Object.entries(currentParams).forEach(([key, value]) => {
        parentPath = parentPath.replace(`:${key}`, value);
      });

      // Try exact match first for the resolved parent path
      const parentConfig = BREADCRUMB_CONFIG.find(item => item.path === parentPath);
      if (parentConfig) {
        trail.unshift({ path: parentConfig.path, labelKey: parentConfig.labelKey });
        current = parentConfig;
      } else {
        // Try to find parent by pattern matching the resolved path
        const parentMatch = matchBreadcrumbPattern(parentPath);
        if (parentMatch) {
          const resolvedParentPath = resolvePath(parentMatch.config.path, parentMatch.params);
          trail.unshift({ path: resolvedParentPath, labelKey: parentMatch.config.labelKey, params: parentMatch.params });
          current = parentMatch.config;
          currentParams = { ...currentParams, ...parentMatch.params };
        } else {
          break;
        }
      }
    }
  }

  return trail;
}
